import { Component, Inject, OnChanges, OnDestroy, OnInit, Input } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { FolderService } from '../../../Core/services/folder.service';
import { DateQueryModel } from '../../../../Common/Forms/models/date-query.model';
import { TagsService } from '../../../../Common/Tags/services/tags.service';
import { FilterService } from '../../../../Common/Filter/services/abstract-filter.service';
import { MonitorConfigService } from '../../../../Common/MonitorShared/services/monitor-config.service';
import { MentionSearchQueryModel } from '../../models/mention-search-query.model';
import { DuplicatesStatusEnum } from '../../../../Common/Filter/enums/duplicates-status.enum';
import { TagGroupsService } from '../../../../Common/Tags/services/tag-groups.service';
import { MediaSubscriptionService } from '../../../Core/services/media-subscription.service';

import { SourceGroupService } from '../../../../Common/Sources/services/source-group.service';
import { SourceService } from '../../../../Common/Sources/services/source.service';
import { ThemeService } from '../../../../Social/Intelligence/services/theme.service';
import { AgentService } from '../../../../Social/Intelligence/services/agent.service';
import { TopicsService } from '../../../Core/services/topics.service';
import { MonitoringSourcePickerFieldComponent } from './monitoring-source-picker-field.component';
import { MentionsNoFacetFilterService } from '../../../../Common/Mentions/services/mentions-no-facet-filter.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { MentionSearchCriteriaModel } from '../../models/mention-search-criteria.model';
import { CachedDateService } from '../../../../Common/MonitorShared/services/cached-date.service';

/**
 * This component is designed to work with pagebuilder as well as being used on its own.
 *
 * It will allow the user to select a datasource from the Monitoring Feature module
 * and emit an event with the type of datasource selected along with the id of the relevant datasource.
 *
 * In the event this component is used with pageBuilders "blockform" component this component will
 * emit the event which will be added to display element of the relevant block.
 */
@Component({
    selector: 'agpr-monitoring-source-picker-without-facet-field',
    templateUrl: './monitoring-source-picker-field.component.html',
    styleUrls: ['./monitoring-source-picker-field.component.scss'],
    providers: [
        MentionsNoFacetFilterService,
        {
            provide: FilterService,
            useExisting: MentionsNoFacetFilterService
        }
    ],
    standalone: false
})
export class MonitoringSourcePickerFieldWithoutFacetComponent extends MonitoringSourcePickerFieldComponent implements OnInit, OnDestroy, OnChanges {

    constructor(
        public topicsService: TopicsService,
        public monitorConfigService: MonitorConfigService,
        public folderService: FolderService,
        public tagsService: TagsService,
        public tagGroupsService: TagGroupsService,
        public mediaSubscriptionService: MediaSubscriptionService,
        public sourceGroupService: SourceGroupService,
        public sourceService: SourceService,
        public themeService: ThemeService,
        public agentService: AgentService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        protected filterService: MentionsNoFacetFilterService,
        public currentUserStateService: CurrentUserStateService,
        public localizationService: LocalizationService,
        public cachedDateService: CachedDateService
    ) {
        super(
            topicsService,
            monitorConfigService,
            folderService,
            tagsService,
            tagGroupsService,
            mediaSubscriptionService,
            sourceGroupService,
            sourceService,
            themeService,
            agentService,
            localizationService,
            currentUserStateService,
            appBaseHref,
            cachedDateService
        );
    }

    ngOnInit() {
        super.ngOnInit();

        if (this.field.orderedAccountFilters) {
            this.filterService.setOrderedAccountFilters(this.field.orderedAccountFilters);
        }
        if (this.field.orderedMercuryFilters) {
            this.filterService.setOrderedMercuryFilters(this.field.orderedMercuryFilters);
        }

        this.getFiltersFromSearch(this.value.filterSearchCriteria);
        this.filterService.setSearchFilters(this.value.filterSearchCriteria);

        this.subscriptions.push(
            this.filterService.getSearchFilters().subscribe(
                (searchCriteria) => {
                    if (!searchCriteria) {
                        return;
                    }

                    this.value.filterSearchCriteria = searchCriteria;
                    this.dateQuery = searchCriteria.query.dateQuery;
                    this.changes.emit(this.value);
                }
            ),
        );

        this.initializeDataSourceOptions();
    }

    setSearchDateQuery(dateQuery: DateQueryModel): void {
        this.filterService.setDateQuery(dateQuery);
    }

    clearSearch(): void {}
    search(): void {}
    setAccountFilters(): void {
        this.filterService.setAccountFilters(this.sourceType);
    }
    populateFiltersFromSearchQuery(filterQuery: MentionSearchQueryModel): void {}

    setDuplicateFilter(duplicatesStatus: DuplicatesStatusEnum): void {
        this.filterService.setDuplicateFilter(duplicatesStatus);
    }

    setSavedFiltersFromIds(ids: number[]): void{
        this.filterService.setSavedFiltersFromIds(ids);
    }

    resetFilters(): void {
        this.filterService.clearFilters();
        this.filterService.applyFiltersToSearch(this.dateQuery);
    }

    getFiltersFromSearch(searchCriteria: MentionSearchCriteriaModel): void {
        if (!searchCriteria) {
            return;
        }
        this.filterService.clearFilters();
        this.setDuplicateFilter(
            searchCriteria.showSimilar ? DuplicatesStatusEnum.show : DuplicatesStatusEnum.hide
        );
        if (searchCriteria.savedFilterIds?.length) {
            this.setSavedFiltersFromIds(searchCriteria.savedFilterIds);
        }
        this.filterService.getFiltersFromSearch(searchCriteria);
    }
}
